(() => {
  const SECTION_SELECTOR = '[data-kalaboost-bestsellers]';
  const FILTER_SELECTOR = '[data-kalaboost-bestsellers-filter]';
  const CARD_SELECTOR = '[data-kalaboost-bestsellers-card]';
  const PRODUCTS_SELECTOR = '[data-kalaboost-bestsellers-products]';
  const TRACK_SELECTOR = '[data-kalaboost-bestsellers-track]';
  const PREV_SELECTOR = '[data-kalaboost-bestsellers-prev]';
  const NEXT_SELECTOR = '[data-kalaboost-bestsellers-next]';
  const SCROLL_UI_SELECTOR = '[data-kalaboost-bestsellers-scroll-ui]';
  const PROGRESS_SELECTOR = '[data-kalaboost-bestsellers-progress]';
  const PROGRESS_FILL_SELECTOR = '[data-kalaboost-bestsellers-progress-fill]';
  const READY_ATTR = 'data-kalaboost-bestsellers-ready';
  const DRAG_THRESHOLD = 4;

  const getCardCategories = (card) =>
    (card.dataset.categories || card.dataset.category || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);

  const cardMatchesCategory = (card, category) => {
    if (!category) return true;
    return getCardCategories(card).includes(category);
  };

  const getScrollContainer = (section) => section.querySelector(PRODUCTS_SELECTOR);

  const getVisibleCards = (scrollContainer) =>
    Array.from(scrollContainer.querySelectorAll(CARD_SELECTOR)).filter(
      (card) => !card.classList.contains('is-hidden') && !card.hasAttribute('hidden')
    );

  const DESKTOP_MQ = '(min-width: 990px)';
  const TABLET_MQ = '(min-width: 750px)';

  const shouldShowNav = (scrollContainer) => {
    const count = getVisibleCards(scrollContainer).length;

    if (window.matchMedia(DESKTOP_MQ).matches) {
      return count > 3;
    }

    if (window.matchMedia(TABLET_MQ).matches) {
      return count > 2;
    }

    return canScroll(scrollContainer);
  };

  const canScroll = (scrollContainer) =>
    scrollContainer.scrollWidth > scrollContainer.clientWidth + 1;

  const getMaxScrollLeft = (scrollContainer) =>
    Math.max(0, scrollContainer.scrollWidth - scrollContainer.clientWidth);

  const getProgressState = (scrollContainer) => {
    const maxScrollLeft = getMaxScrollLeft(scrollContainer);

    if (maxScrollLeft <= 0) {
      return { width: 100, offset: 0, value: 100 };
    }

    const thumbWidth = Math.min(
      100,
      (scrollContainer.clientWidth / scrollContainer.scrollWidth) * 100
    );
    const ratio = Math.min(1, Math.max(0, scrollContainer.scrollLeft / maxScrollLeft));
    const offset = ratio * (100 - thumbWidth);

    return {
      width: thumbWidth,
      offset,
      value: Math.min(100, Math.round(offset + thumbWidth)),
    };
  };

  const setActiveFilter = (section, category) => {
    const filters = section.querySelectorAll(FILTER_SELECTOR);
    const cards = section.querySelectorAll(CARD_SELECTOR);
    const scrollContainer = getScrollContainer(section);

    section.dataset.activeCategory = category;

    filters.forEach((filter) => {
      const isActive = filter.dataset.category === category;
      filter.classList.toggle('is-active', isActive);
      filter.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    cards.forEach((card) => {
      const matches = cardMatchesCategory(card, category);
      card.classList.toggle('is-hidden', !matches);

      if (matches) {
        card.removeAttribute('hidden');
      } else {
        card.setAttribute('hidden', '');
      }
    });

    if (scrollContainer) {
      scrollContainer.scrollTo({ left: 0, behavior: 'auto' });
    }

    section._kalaboostBestsellersUpdateScrollUi?.();
  };

  const onFilterClick = (event) => {
    const filter = event.target.closest(FILTER_SELECTOR);
    if (!filter) return;

    const section = filter.closest(SECTION_SELECTOR);
    if (!section) return;

    const category = filter.dataset.category;
    if (!category || category === section.dataset.activeCategory) return;

    setActiveFilter(section, category);
  };

  const onBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const card = target.closest(CARD_SELECTOR);
    const filter = target.closest(FILTER_SELECTOR);
    const section = target.closest(SECTION_SELECTOR) || target.querySelector?.(SECTION_SELECTOR);

    if (!section) return;

    if (card) {
      const categories = getCardCategories(card);
      if (!categories.length) return;

      const activeCategory = section.dataset.activeCategory;
      const preferred = categories.includes(activeCategory) ? activeCategory : categories[0];
      setActiveFilter(section, preferred);
      return;
    }

    if (filter?.dataset.category) {
      setActiveFilter(section, filter.dataset.category);
    }
  };

  const initCarousel = (section) => {
    const scrollContainer = getScrollContainer(section);
    if (!scrollContainer || scrollContainer.dataset.kalaboostBestsellersCarouselReady === 'true') {
      return;
    }

    const prevButton = section.querySelector(PREV_SELECTOR);
    const nextButton = section.querySelector(NEXT_SELECTOR);
    const scrollUi = section.querySelector(SCROLL_UI_SELECTOR);
    const progressBar = section.querySelector(PROGRESS_SELECTOR);
    const progressFill = section.querySelector(PROGRESS_FILL_SELECTOR);
    const track = scrollContainer.querySelector(TRACK_SELECTOR);

    let isPointerDown = false;
    let isDragging = false;
    let preventClick = false;
    let startX = 0;
    let startScrollLeft = 0;
    let activePointerId = null;
    let scrollEndTimer = null;

    const updateScrollUi = () => {
      const showNav = shouldShowNav(scrollContainer);
      const maxScrollLeft = getMaxScrollLeft(scrollContainer);
      const atStart = scrollContainer.scrollLeft <= 2;
      const atEnd = scrollContainer.scrollLeft >= maxScrollLeft - 2;

      scrollContainer.classList.toggle('is-scrollable', showNav && canScroll(scrollContainer));

      if (prevButton) {
        prevButton.classList.toggle('is-visible', showNav);
        prevButton.disabled = !showNav || atStart;
      }

      if (nextButton) {
        nextButton.classList.toggle('is-visible', showNav);
        nextButton.disabled = !showNav || atEnd;
      }

      if (scrollUi) {
        scrollUi.classList.toggle('is-visible', showNav);
      }

      if (progressFill && showNav) {
        const progressState = getProgressState(scrollContainer);
        progressFill.style.left = `${progressState.offset}%`;
        progressFill.style.width = `${progressState.width}%`;

        if (progressBar) {
          progressBar.setAttribute('aria-valuenow', String(progressState.value));
        }
      } else if (progressFill) {
        progressFill.style.left = '0%';
        progressFill.style.width = '0%';

        if (progressBar) {
          progressBar.setAttribute('aria-valuenow', '0');
        }
      }
    };

    const releaseDocumentListeners = () => {
      document.removeEventListener('pointermove', onDocumentPointerMove);
      document.removeEventListener('pointerup', onDocumentPointerUp);
      document.removeEventListener('pointercancel', onDocumentPointerUp);
    };

    const stopDragging = () => {
      isPointerDown = false;
      isDragging = false;
      activePointerId = null;
      scrollContainer.classList.remove('is-dragging');
      releaseDocumentListeners();
    };

    const onPointerMove = (event) => {
      if (!isPointerDown || event.pointerId !== activePointerId || !shouldShowNav(scrollContainer)) return;
      if (event.pointerType === 'touch') return;

      const deltaX = event.clientX - startX;

      if (!isDragging && Math.abs(deltaX) > DRAG_THRESHOLD) {
        isDragging = true;
        preventClick = true;
        scrollContainer.classList.add('is-dragging');
      }

      if (!isDragging) return;

      event.preventDefault();
      scrollContainer.scrollLeft = startScrollLeft - deltaX;
      window.requestAnimationFrame(updateScrollUi);
    };

    const onDocumentPointerMove = (event) => {
      onPointerMove(event);
    };

    const onDocumentPointerUp = (event) => {
      if (event.pointerId !== activePointerId) return;
      stopDragging();
      updateScrollUi();
    };

    const onPointerDown = (event) => {
      if (!shouldShowNav(scrollContainer)) return;
      if (event.pointerType === 'touch') return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      isPointerDown = true;
      isDragging = false;
      activePointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = scrollContainer.scrollLeft;

      document.addEventListener('pointermove', onDocumentPointerMove);
      document.addEventListener('pointerup', onDocumentPointerUp);
      document.addEventListener('pointercancel', onDocumentPointerUp);
    };

    const onClickCapture = (event) => {
      if (!preventClick) return;
      preventClick = false;
      event.preventDefault();
      event.stopPropagation();
    };

    const onScroll = () => {
      window.requestAnimationFrame(updateScrollUi);
      window.clearTimeout(scrollEndTimer);
      scrollEndTimer = window.setTimeout(updateScrollUi, 100);
    };

    const getScrollStep = () => {
      const visibleCards = getVisibleCards(scrollContainer);
      const firstCard = visibleCards[0];
      if (!firstCard || !track) return 0;

      const trackStyles = window.getComputedStyle(track);
      const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;

      return firstCard.offsetWidth + gap;
    };

    const scrollNext = () => {
      if (!shouldShowNav(scrollContainer)) return;

      const step = getScrollStep();
      if (!step) return;

      scrollContainer.scrollBy({
        left: step,
        behavior: 'smooth',
      });
    };

    const scrollPrev = () => {
      if (!shouldShowNav(scrollContainer)) return;

      const step = getScrollStep();
      if (!step) return;

      scrollContainer.scrollBy({
        left: -step,
        behavior: 'smooth',
      });
    };

    scrollContainer.addEventListener('pointerdown', onPointerDown);
    scrollContainer.addEventListener('click', onClickCapture, true);
    scrollContainer.addEventListener('scroll', onScroll, { passive: true });
    scrollContainer.addEventListener(
      'dragstart',
      (event) => {
        event.preventDefault();
      },
      true
    );

    prevButton?.addEventListener('click', scrollPrev);
    nextButton?.addEventListener('click', scrollNext);

    window.addEventListener('resize', () => {
      window.requestAnimationFrame(updateScrollUi);
    });

    updateScrollUi();
    scrollContainer.dataset.kalaboostBestsellersCarouselReady = 'true';

    section._kalaboostBestsellersUpdateScrollUi = updateScrollUi;
  };

  const initSection = (section) => {
    if (!(section instanceof Element)) return;
    if (section.getAttribute(READY_ATTR) === 'true') return;

    const filtersRoot = section.querySelector('[data-kalaboost-bestsellers-filters]');

    if (filtersRoot) {
      filtersRoot.addEventListener('click', onFilterClick);
    }

    initCarousel(section);

    const activeCategory =
      section.dataset.activeCategory ||
      section.querySelector(`${FILTER_SELECTOR}.is-active`)?.dataset.category ||
      section.querySelector(FILTER_SELECTOR)?.dataset.category;

    if (activeCategory) {
      setActiveFilter(section, activeCategory);
    } else {
      section._kalaboostBestsellersUpdateScrollUi?.();
    }

    section.setAttribute(READY_ATTR, 'true');
  };

  const destroySection = (section) => {
    if (!(section instanceof Element)) return;

    const filtersRoot = section.querySelector('[data-kalaboost-bestsellers-filters]');
    filtersRoot?.removeEventListener('click', onFilterClick);

    const scrollContainer = getScrollContainer(section);
    if (scrollContainer) {
      scrollContainer.removeAttribute('data-kalaboost-bestsellers-carousel-ready');
    }

    delete section._kalaboostBestsellersUpdateScrollUi;
    section.removeAttribute(READY_ATTR);
  };

  const initAll = (root = document) => {
    root.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof Element)) return;
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof Element)) return;
    event.target.querySelectorAll(SECTION_SELECTOR).forEach(destroySection);
    if (event.target.matches?.(SECTION_SELECTOR)) {
      destroySection(event.target);
    }
  });

  document.addEventListener('shopify:block:select', onBlockSelect);
})();
